import cookie from "js-cookie";
import axios, { type AxiosInstance } from "axios";
import { store } from "@/redux/store";
import { logout } from "@/redux/features/auth-slice";
import {
  isPublicAuthPath,
  isSessionExpiredPayload,
  redirectToLoginOnSessionExpired,
} from "@/utils/auth-session";

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: "",
  });

  client.interceptors.request.use(
    (config) => {
      // Regular-user tokens are httpOnly (attached server-side by /api/[...path]) — this only ever
      // finds a value for the super-admin flow, which still manages its own client-readable cookie.
      const superAdminToken = cookie.get("super_admin_access_token");
      if (superAdminToken && !config.headers.get("Authorization")) {
        config.headers.set("Authorization", `Bearer ${superAdminToken}`);
      }

      if (!(config.data instanceof FormData)) {
        config.headers.set("Content-Type", "application/json");
      } else {
        // Let the browser set multipart boundary — a preset Content-Type breaks form fields
        config.headers.delete("Content-Type");
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      const axiosError = error as {
        response?: {
          status?: number;
          headers?: Record<string, unknown>;
          data?: { error_code?: string; message?: string; detail?: string };
        };
        status?: number;
      };
      const status = axiosError.response?.status ?? axiosError.status;
      const data = axiosError.response?.data;

      // Many backends return 401 UNAUTHORIZED for permission/resource denial while the
      // session is still valid. Only force logout for true session-expiry auth failures.
      const isAuthFailure =
        status === 401 ||
        String(data?.error_code ?? "").toUpperCase() === "AUTHENTICATION_ERROR";

      if (
        isAuthFailure &&
        isSessionExpiredPayload(data, axiosError.response?.headers) &&
        typeof window !== "undefined" &&
        !isPublicAuthPath(window.location.pathname)
      ) {
        store.dispatch(logout());

        const onSuperAdminApp = window.location.pathname.startsWith("/super-admin");
        if (onSuperAdminApp) {
          cookie.remove("super_admin_access_token", { path: "/" });
          cookie.remove("super_admin_refresh_token", { path: "/" });
        }

        redirectToLoginOnSessionExpired();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

const apiClient = createApiClient();

export default apiClient;
